import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { GeneratedScript, generatedContentApi } from 'src/api/generatedContent';
import styles from './EditDialog.module.scss';

interface ScriptEditDialogProps {
  open: boolean;
  onClose: () => void;
  script: GeneratedScript | null;
  onScriptUpdated: () => void;
}

const ScriptEditDialog = ({
  open,
  onClose,
  script,
  onScriptUpdated,
}: ScriptEditDialogProps) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (script) {
      setTitle(script.title || '');
      setContent(script.content || '');
    }
  }, [script, open]);

  const handleSave = async () => {
    if (!script) {
      return;
    }

    try {
      setIsSaving(true);

      await generatedContentApi.updateGeneratedScript({
        scriptId: script._id,
        title,
        content,
      });
      toast.success('Script updated successfully');

      // refresh data in content card
      onScriptUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating script:', error);
      toast.error('Failed to update script');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle className={styles.dialogHeader}>Edit Script</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          variant="outlined"
          className={styles.formControl}
        />

        <TextField
          label="Content"
          fullWidth
          multiline
          rows={15}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          margin="normal"
          variant="outlined"
          className={styles.contentField}
        />
      </DialogContent>

      <DialogActions>
        <button onClick={onClose} className={styles.cancelButton}>
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={styles.loadingButton}
        >
          {isSaving ? (
            <>
              <span style={{ visibility: 'hidden' }}>Save Changes</span>
              <CircularProgress size={24} className={styles.loadingIndicator} />
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default ScriptEditDialog;
